/**
 * WhyParachPay - Marketing section showcasing value proposition
 * Placed below hero, above footer. Mobile-first, dark/light mode support.
 */
import { 
  Gift,
  ArrowLeftRight,
  Globe,
  Users
} from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';

// Check if running as native app (hide store badges in native)
const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

const WhyParachPay = () => {
  const { t } = useTranslation();
  const googlePlayUrl = 'https://play.google.com/store/apps/details?id=com.parachpay.app';
  const appGalleryUrl = 'https://appgallery.huawei.com/app/C118318155';
  const showStoreBadges = !isNativeApp();

  const CARDS = [
    {
      icon: Gift,
      title: t('home.why.card1Title'),
      description: t('home.why.card1Desc')
    },
    {
      icon: ArrowLeftRight,
      title: t('home.why.card2Title'),
      description: t('home.why.card2Desc')
    },
    {
      icon: Globe,
      title: t('home.why.card3Title'),
      description: t('home.why.card3Desc')
    },
    {
      icon: Users,
      title: t('home.why.card4Title'),
      description: t('home.why.card4Desc')
    }
  ];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-muted/20 to-background">
      <div className="max-w-6xl mx-auto">
        {/* Headline & Subhead */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 text-foreground">
            {t('home.why.title')}
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg lg:text-xl max-w-3xl mx-auto leading-relaxed">
            {t('home.why.subtitle')}
          </p>
        </div>

        {/* Four Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mb-12 md:mb-16">
          {CARDS.map((card, index) => {
            const Icon = card.icon;
            return (
              <div
                key={`why-card-${index}`}
                className="group p-6 rounded-2xl bg-card border border-border hover:border-emerald-500/40 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors">
                  <Icon className="w-6 h-6 text-emerald-600 dark:text-emerald-400 group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2 text-foreground">
                  {card.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Closing Line */}
        <div className="text-center mb-10">
          <p className="text-lg sm:text-xl lg:text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
            {t('home.why.closing')}
          </p>
        </div>

        {/* App Store Buttons */}
        {showStoreBadges && (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={googlePlayUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-foreground text-background px-6 py-3 rounded-xl hover:opacity-90 transition-opacity shadow-lg"
              aria-label="Get ParachPay on Google Play"
            >
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current" aria-hidden="true">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/>
              </svg>
              <div className="text-left">
                <div className="text-xs opacity-80">{t('home.why.getItOn')}</div>
                <div className="text-base font-semibold">Google Play</div>
              </div>
            </a>
            <a
              href={appGalleryUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex rounded-xl hover:opacity-90 transition-opacity shadow-lg"
              data-testid="why-huawei-appgallery-badge"
            >
              <img src="/badges/huawei-appgallery-badge.png" alt={t('home.badges.huaweiAlt')} className="h-16 w-auto" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};

export default WhyParachPay;
